import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateCustomerAsync } from "../../redux/features/customer/customerThunks";
import * as Yup from "yup";
import { useFormik } from "formik";

// Validation schema using Yup
const validationSchema = Yup.object({
  phoneNumber: Yup.string()
    .matches(/^\d{8}$/, "Phone number must be exactly 8 digits")
    .required("Phone number is required"),
  firstName: Yup.string()
    .required("First name is required")
    .max(50, "First name must be less than 50 characters"),
  lastName: Yup.string()
    .required("Last name is required")
    .max(50, "Last name must be less than 50 characters"),
});

const EditUserModal = ({ isOpen, onClose, userData }) => {
  const dispatch = useDispatch();
  
  const formik = useFormik({
    initialValues: {
      countryCode: "+965 (KW)",
      phoneNumber: userData?.mobile_no || "",
      firstName: userData?.first_name || "",
      lastName: userData?.last_name || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const updateData = {
        id: userData.id,
        first_name: values.firstName,
        last_name: values.lastName,
        mobile_no: values.phoneNumber,
        mobile_country_code: "+965",
      };

      try {
        await dispatch(updateCustomerAsync(updateData));
        onClose();
      } catch (error) {
        console.error("Failed to update user:", error);
      }
    },
    enableReinitialize: true,
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg p-6 w-[350px] relative">
        {/* Close button - using the provided style */}
        <button 
          className="absolute -top-3 -right-3 bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-700 focus:outline-none"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>

        <h2 className="text-xl text-gray-500 mb-4 text-center font-bold">
          Edit User Details
        </h2>

        <form onSubmit={formik.handleSubmit}>
          <div className="space-y-6">
            {/* Phone Number Row */}
            <div className="flex space-x-2">
              {/* Country Code */}
              <div className="flex-shrink-0">
                <select
                  name="countryCode"
                  value={formik.values.countryCode}
                  onChange={formik.handleChange}
                  disabled
                  className="border border-slate-300 bg-neutral-50 rounded-xl px-4 py-2 w-[110px] h-12 appearance-none"
                >
                  <option value="+965 (KW)">+965 (KW)</option>
                </select>
              </div>

              {/* Phone Number */}
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    name="phoneNumber"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Phone Number"
                    className="border border-slate-300 bg-neutral-50 rounded-xl px-4 py-2 w-full h-12"
                  />
                </div>
                {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                  <div className="text-red-500 text-xs mt-1 min-h-[16px]">
                    {formik.errors.phoneNumber}
                  </div>
                )}
              </div>
            </div>

            {/* Name Row */}
            <div className="flex space-x-2">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    name="firstName"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="First Name"
                    className="border border-slate-300 bg-neutral-50 rounded-xl px-4 py-2 w-full h-12"
                  />
                </div>
                {formik.touched.firstName && formik.errors.firstName && (
                  <div className="text-red-500 text-xs mt-1 min-h-[16px]">
                    {formik.errors.firstName}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    name="lastName"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Last Name"
                    className="border border-slate-300 bg-neutral-50 rounded-xl px-4 py-2 w-full h-12"
                  />
                </div>
                {formik.touched.lastName && formik.errors.lastName && (
                  <div className="text-red-500 text-xs mt-1 min-h-[16px]">
                    {formik.errors.lastName}
                  </div>
                )}
              </div>
            </div>

            {/* Approve Changes Button */}
            <div className="flex justify-center mt-4">
              <button
                type="submit"
                disabled={!formik.isValid || formik.isSubmitting}
                className={`bg-amber-800 text-white font-bold rounded-lg py-2 px-6 h-12 ${
                  !formik.isValid || formik.isSubmitting ? "opacity-70" : ""
                }`}
              >
                {formik.isSubmitting ? "Updating..." : "Approve Changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal; 